import _ from 'lodash';
import { json2csv } from 'json-2-csv';
import MD5 from 'crypto-js/md5';
import { Base64 } from 'js-base64';
import { Matcher } from '../types/rest/dslTypes';
import i18n from '../i18n';
import { MockService } from '../types/rest/mockServiceTypes';
import { MockServiceTemplate } from '../store/mockServiceTemplates/mockServiceTemplateTypes';

/**
 * Retrieves the key associated with the given value in a JSON object.
 *
 * @param {Object} jsonObject - The JSON object to search.
 * @param {*} value - The value to find the key for.
 * @returns {*} The key associated with the value, or null if not found.
 */
export const getKeyByValue = (jsonObject: any, value: any) => {
  if (value == null) {
    return value;
  } else {
    let result: any = null;
    Object.keys(jsonObject).forEach(function(key) {
      if (value === jsonObject[key]) {
        result = key;
        return;
      }
    });
    return result;
  }
};

export const cacheControlHeaders = { 'Cache-Control': 'no-cache, no-store, must-revalidate' };

/**
 * Truncates a string to a specified length and appends an ellipsis if it exceeds that length.
 *
 * @param {string} name - The string to be truncated.
 * @param {number} len - The maximum length of the string.
 * @returns {string} The truncated string with an ellipsis if it exceeds the specified length.
 */
export const getShortString = (name: string, len: number): string => {
  if (name.length >= len) {
    name = name.substring(0, len);
    name = name + '...';
  }
  return name;
};

/**
 * Takes json payload and forces browser to download it as CSV file with specified filename.
 *
 * @param jsonPayload - payload to be transformed to csv.
 * @param fileName - filename to be used for save.
 */
export const downloadJsonPayloadAsCsv = (jsonPayload: any, fileName: string) => {
  json2csv(jsonPayload, (err: any, dataCvsFormat: any) => {
    if (err) throw err;
    forceBrowserToDownloadData(dataCvsFormat, fileName.replace('.json', '.csv'));
  });
};

/**
 * Forces browser to download content provided by data parameter, saves it with fileName.
 *
 * @param data
 * @param fileName
 */
export const forceBrowserToDownloadData = (data: any, fileName: string) => {
  const downloadUrl = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

/**
 * Gets random integer between specified values.
 *
 * @param min
 * @param max
 */
export const getRandomInt = (min: number, max: number) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min)) + min;
};

/**
 * Deep diff between two object, using lodash. The result is union of properties which differ in compared objects.
 * @param  {Object} object Object compared
 * @param  {Object} base   Object to compare with
 * @return {Object}        Return a new object who represent the diff
 * @return ignoreFields    list of attributes to be ignored from comparison, they will be included in the final diff object.
 */
export const getDifference = (object: any, base: any, ignoreFields?: string[]) => {
  function changes(object: any, base: any) {
    return _.transform(object, function(result: any, value: any, key: any) {
      if (!_.isEqual(value, base[key])) {
        result[key] = _.isObject(value) && _.isObject(base[key]) ? changes(value, base[key]) : value;
      }
    });
  }
  return changes(object, base);
};

/**
 * Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
 * @param {String} text The text to be rendered.
 * @param {String} font The css font descriptor that text is to be rendered with (e.g. "bold 14px verdana").
 *
 * @see https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
 */
let textCanvas: any;
export const getTextWidth = (text: any, font: any): any => {
  // re-use canvas object for better performance
  const canvas: any = textCanvas || (textCanvas = document.createElement('canvas'));
  const context: any = canvas.getContext('2d');
  context.font = font;

  const metrics: any = context.measureText(text);
  return metrics.width;
};

/**
 * Updates an object in an array immutably.
 *
 * @param {any[]} array - The array containing the objects.
 * @param {any} itemToReplace - The new object to replace the old one.
 * @param {number} index - The index of the object to replace.
 * @returns {any[]} A new array with the updated object.
 *
 * @see https://redux.js.org/recipes/structuring-reducers/immutable-update-patterns
 */
export const updateObjectInArray = (array: any[], itemToReplace: any, index: number): any[] => {
  return array.map(function(item: any, idx: number) {
    if (idx !== index) {
      // This isn't the item we care about - keep it as-is
      return item;
    }
    // Otherwise, this is the one we want - return an updated value
    return {
      ...item,
      ...itemToReplace,
    };
  });
};

export const isBase64 = (value: string) => {
  try {
    return btoa(atob(value)) === value;
  } catch (err) {
    return false;
  }
};

/**
 * Generates an MD5 hash from a given string.
 *
 * @param {string} str - The input string to hash.
 * @param {number} [length] - Optional length to truncate the hash.
 * @returns {string} The generated hash, truncated if length is specified.
 */
const getHashFromString = (str: string, length?: number): string => {
  const hash = MD5(str).toString();
  if (_.isNil(length)) {
    return hash;
  }
  return hash.substring(0, length);
};

/**
 * Creates MD5 hashing function from the string.
 * @param str
 * @param length - optional length
 */
export const getHashCode = (str: string, length?: number) => {
  if (_.isNil(str)) {
    return getHashFromString('', length);
  }
  return getHashFromString(str, length);
};

/**
 * Converts an XPath string to a user-friendly format.
 *
 * @param {string} xpath - The XPath string to be converted.
 * @returns {string} The user-friendly XPath string.
 */
export const getUserFriendlyXpath = (xpath: string): string => {
  const tokens = xpath.split('/');
  try {
    return _.map(tokens, (token, index) => {
      const replacedString = token.replace(/[^:]+:/, '');
      const divider = index !== 0 && index !== tokens.length - 1 ? ' > ' : '';
      return replacedString + divider;
    }).join('');
  } catch (error) {
    return xpath;
  }
};

export const getMatcherType = (matcher: Matcher) => {
  switch (matcher) {
    case Matcher.matches_xml:
      return 'xpath';
    case Matcher.matches_xml_cdata:
      return 'cdata_xpath';
    case Matcher.matches_json:
      return 'json';
  }
  return null;
};

/**
 * Gets error message from server response / error object.
 * @param error
 */
export const getErrorMessage = (error: any) => {
  //Try to get a single error message
  const msg = _.get(error, 'response.data.error', error.toString());
  if (!_.isNil(msg)) {
    return msg;
  }

  //If a single message is empty, try to load an array of errors
  const msgs = _.get(error, 'response.data.errors', error.toString());
  if (!_.isNil(msgs) && _.size(msgs) !== 0) {
    return _.map(msgs, (item: { message: string; severity: string }) => {
      return item.message;
    }).toString();
  }

  return error.toString();
};

/**
 * Capitalizes first letter of the string, the rest of fields is transformed to lowercase.
 * @param value
 */
export const capitalizeFirst = (value: string) => {
  return _.startCase(_.toLower(value));
};

/**
 * Transforms number to more user-friendly format.
 * @param number
 */
export const getFriendlyNumberFormat = (number?: number | string) => {
  if (_.isNil(number)) {
    return '';
  }
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

/**
 * Creates id for response wizard widget from row data object.
 * @param rowData
 */
export const getIdForResponseWizard = (rowData: Partial<{ id: string | number; name: string }>) => {
  return rowData.name + '_' + rowData.id;
};

/**
 * Convert string to base64
 *
 * @param input
 */
export const b64encode = (input: number[] | string): string => {
  if (typeof input === 'string') {
    return Base64.encode(input);
  } else {
    return Base64.fromUint8Array(new Uint8Array(input));
  }
};

export const isCancelTokenErrorMessage = (message: string) => {
  return _.includes(message, 'Fetching Transactions cancelled');
};

export interface BaseEntity {
  id: number;
  [key: string]: any;
}

/**
 * Moves specified elements to the beginning of the array.
 *
 * @param {BaseEntity[]} arr - The array to modify.
 * @param {BaseEntity[]} elementsToMove - The elements to move to the beginning.
 * @returns {BaseEntity[]} The modified array with specified elements moved to the beginning.
 */
export const moveElementToBeginning = (arr: BaseEntity[], elementsToMove: BaseEntity[]): BaseEntity[] => {
  // Nothing to move
  if (_.size(elementsToMove) === 0) {
    return arr;
  }
  // We need to reverse the order when moving to start to the order
  // of selected elements is kept same
  const reverseOrder = _.reverse([...elementsToMove]);
  _.forEach(reverseOrder, element => {
    const index = _.findIndex(arr, el => el.id === element.id);
    if (index !== -1) {
      // Remove the element from its current position
      arr.splice(index, 1);
      // Add the element to the beginning of the array
      arr.unshift(element);
    }
  });
  return arr;
};

/**
 * Moves specified elements to the end of the array.
 *
 * @param {BaseEntity[]} arr - The array to modify.
 * @param {BaseEntity[]} elementsToMove - The elements to move to the end.
 * @returns {BaseEntity[]} The modified array with specified elements moved to the end.
 */
export const moveElementsToEnd = (arr: BaseEntity[], elementsToMove: BaseEntity[]): BaseEntity[] => {
  const arrCopy = [...arr];

  _.forEach(elementsToMove, element => {
    const index = _.findIndex(arrCopy, el => element.id === el.id);
    if (index !== -1) {
      // Remove the element from its current position
      arrCopy.splice(index, 1);
      // Add the element to the end of the array
      arrCopy.push(element);
    }
  });

  return arrCopy;
};

/**
 * Creates title for our tables (showing 'Y OF X SELECTED')
 * @param total
 * @param selected
 */
export const getSelectedCountTitle = (total: number, selected: number) => {
  if (selected === 0) {
    return `(${total})`;
  } else {
    return `(${selected} ${i18n.t('common:selectAll.noOfSelected', {
      total: total,
    })})`;
  }
};

export const getPreferredEndpoint = (preferredEndpoints: { httpEndpoint: string; httpsEndpoint: string }): string => {
  if (!_.isNil(preferredEndpoints.httpsEndpoint)) {
    return preferredEndpoints.httpsEndpoint;
  } else if (!_.isNil(preferredEndpoints.httpEndpoint)) {
    return preferredEndpoints.httpEndpoint;
  }
  return null;
};

const stringToHash = (input: string): number => {
  let hash = 5381; // Starting with a large prime number
  for (let i = 0; i < input.length; i++) {
    hash = (hash * 33) ^ input.charCodeAt(i); // Using bitwise XOR and a prime multiplier
  }
  return hash >>> 0; // Ensure positive 32-bit integer (unsigned)
};

/**
 * Gets random number from 0 to {max} based on a string. This approach is deterministic,
 * meaning the same input will always result in the same random number.
 * @param inputString
 * @param maxValue
 */
export const getRandomNumberFromString = (inputString: string, maxValue: number): number => {
  const hash = stringToHash(inputString);
  return Math.abs(hash % maxValue); // Ensure the number is between 1 and max value
};

/**
 * Check if the entity is template or not.
 */
export const isTemplate = (mockServiceEntity: MockService | MockServiceTemplate) => {
  //Templates does not have isNew property
  return !mockServiceEntity.hasOwnProperty('isNew');
};

export const isEntityHttpMockService = (entity: MockService | MockServiceTemplate) => {
  //Templates are not supported for messaging type yet
  if (isTemplate(entity)) {
    return false;
  }
  if ((entity as MockService).httpRunnerEnabled === true) {
    return true;
  }
};

/**
 * Checks if a provided array is an array of strings
 * @param array
 */
export const isArrayOfStrings = (array: unknown): array is string[] => {
  return Array.isArray(array) && array.every(item => typeof item === 'string');
};

/**
 * Use the enum object to find the key that matches the string:
 * @param enumObj
 * @param value
 */
export const getEnumValueByString = <T>(enumObj: T, value: string | number): T[keyof T] | undefined =>
  Object.values(enumObj).includes(value as T[keyof T]) ? (value as T[keyof T]) : undefined;
